/**
 * The booth: one announcer voice at a time, with priorities, a short queue and ducking.
 *
 * Rules (AnnounceOptions):
 *   - a higher-priority line interrupts the current one — at its next PHRASE BREAK when one is close
 *     (`BoothSources.breaks`: "SPIN MOVE!! | Oh, that's nasty!" → "SPIN MOVE!! — OVER THE TOP!!", never "SPI— OVER
 *     THE TOP!!"; `BREAK_WAIT_S`, shorter for a much bigger call), else right away (90 ms fade-out, the new line
 *     enters 50 ms in, so the voice never stacks on itself). A line about to end (`INTERRUPT_GRACE_S`, or with no
 *     break left and less than the wait to go) finishes instead and the new line follows it seamlessly;
 *   - an equal-priority line queues behind it (queue of 3; a queued line expires after its `wait` — default
 *     `QUEUE_TTL_S`: a bark about a play that ended seconds ago is worse than silence, while a play's result may
 *     wait a little longer for the hype call ahead to finish). A `chain`ed line belongs to the lines ahead ("HE'S
 *     ON FIRE!!" after "TOUCHDOWN!!!"): its timeout starts when they are projected to finish, it never cuts a line
 *     mid-phrase, and it is dropped with the queued line it was following if that one goes stale;
 *   - a lower-priority line is dropped while someone is speaking (a chained one queues: it was asked to follow;
 *     so does one given its own `wait` when the booth will be free within it).
 * While a ducking line plays, music and crowd duck (fast attack, slow release after the line).
 * Lines go through a "stadium PA" chain owned by the engine (presence EQ + a reverb send).
 *
 * Timing is measured on what is HEARD: the line files end with ~0.25 s of padding (`BoothSources.tail`), so a line
 * "ends" when its last word does, and the next queued line follows right there (after a `CHAIN_BREATH_S` breath),
 * scheduled sample-accurately up to one pump ahead instead of waiting for the next 100 ms pump. The silent tail of
 * the previous file keeps playing under it (no cut, so no click).
 */
import type { AnnounceOptions, AnnouncedLine } from './index';

export const QUEUE_TTL_S = 1.6;
export const MAX_QUEUE = 3;
/** Fade-out of an interrupted line (s). */
export const INTERRUPT_FADE_S = 0.09;
/** The interrupting line starts this long after the cut begins (s): a breath, never two voices at once. */
export const INTERRUPT_ENTRY_S = 0.05;
/** A line with at most this much left (s) finishes; a higher-priority line follows right after it. */
export const INTERRUPT_GRACE_S = 0.45;
/** Pause between a line's last word and the queued line that follows it (s). */
export const CHAIN_BREATH_S = 0.06;
/** How far ahead of a line's end the follow-up is scheduled (s; > the engine's 100 ms pump). */
export const CHAIN_LOOKAHEAD_S = 0.14;
/** Most trailing padding ever trimmed from a line (s). */
export const MAX_TAIL_S = 0.4;
/** A bigger call waits at most this long (s) for the current line's next phrase break before cutting it. */
export const BREAK_WAIT_S = 1.1;
/** …and this long when it outranks the current line by 2 or more (TOUCHDOWN over colour: get there fast). */
export const BIG_BREAK_WAIT_S = 0.6;
/** The bigger call starts this long after the phrase break (the cut line's fade-out). */
export const BREAK_FADE_S = 0.04;
/** A line younger than this (s) that must be cut right away is swapped out with a quick `FRESH_FADE_S` fade. */
export const FRESH_S = 0.25;
export const FRESH_FADE_S = 0.025;

export interface BoothSources {
  buffer(id: string): AudioBuffer | null;
  caption(id: string): string | null;
  /** Known duration (s) before decode, for caption timing. */
  duration(id: string): number;
  /** Trailing silence of a line's file (s; 0 when unknown). The line counts as over when its last word is. */
  tail?(id: string): number;
  /**
   * Phrase breaks of a line (s from the start of its file, ascending): where a pause between two phrases begins
   * ("SPIN MOVE!! | Oh, that's nasty!"). A bigger call cuts the line there instead of mid-word. None = no break.
   */
  breaks?(id: string): readonly number[];
  trim(id: string): number;
  /** Start ducking now until `until` (context time); `release` after. `priority` = the line's (big calls duck more). */
  duck(until: number, priority: number): void;
  /** Whether sound can play now (unlocked, not muted by suspension). */
  audible(): boolean;
  /**
   * A line starts (its caption). `at` (context time) is when its first sound is scheduled when that is later than
   * now (a bigger call waiting for a phrase break, a follow-up scheduled ahead of the pump).
   */
  emit(line: AnnouncedLine, at?: number): void;
  /**
   * A line stopped sounding: it finished (`cut` false) or was interrupted / hushed (`cut` true). `at` (context
   * time) is when its sound actually stops when that is later than now (cut at its next phrase break).
   */
  ended?(id: string, cut: boolean, at?: number): void;
}

interface Speaking {
  id: string;
  priority: number;
  src: AudioBufferSourceNode;
  gain: GainNode;
  /** When the source starts (context time; later than now for a follow-up scheduled ahead). */
  startAt: number;
  /** When its last word ends (context time; the file's silent tail is excluded). */
  endsAt: number;
  duck: boolean;
}

interface Queued {
  id: string;
  priority: number;
  duck: boolean;
  expires: number;
  /** A chained line follows this queued one: if that one goes stale, so does this. */
  after?: Queued;
  /** Removed from the queue without being said. */
  dropped?: boolean;
}

export class Booth {
  private speaking: Speaking | null = null;
  /** The line that finished last, still playing its silent tail (faded on hush). */
  private tailing: Speaking | null = null;
  private readonly queue: Queued[] = [];

  constructor(
    private readonly ctx: BaseAudioContext,
    private readonly dest: AudioNode,
    private readonly src: BoothSources,
  ) {}

  get current(): string | null {
    return this.speaking?.id ?? null;
  }

  get queued(): number {
    return this.queue.length;
  }

  /** Returns the caption if the line was accepted (played now or queued), else null. */
  say(id: string, opts: AnnounceOptions = {}): string | null {
    const caption = this.src.caption(id);
    if (caption === null) return null;
    const priority = opts.priority ?? 1;
    const duck = opts.duck ?? true;
    const now = this.ctx.currentTime;
    // settle first: a line whose last word passed is over, and whatever was waiting behind it goes on
    this.pump(now);
    const cur = this.speaking;
    if (cur && cur.endsAt > now) {
      if (priority > cur.priority) {
        if (cur.id === id) return null;
        if (cur.startAt > now + 1e-4) {
          // a follow-up scheduled ahead has not made a sound yet: the bigger call takes its slot (right after
          // the previous line's last word), the follow-up is dropped like any lower queued line
          const at = cur.startAt;
          this.cut(cur, now, 0.005);
          this.dropWhere((q) => q.priority < priority || q.id === id);
          this.start(id, priority, duck, now, at - now);
          return caption;
        }
        // queued lines below the new priority are stale now
        this.dropWhere((q) => q.priority < priority);
        const left = cur.endsAt - now;
        const maxWait = priority - cur.priority >= 2 ? BIG_BREAK_WAIT_S : BREAK_WAIT_S;
        const brk = this.nextBreak(cur, now, maxWait);
        if (left <= INTERRUPT_GRACE_S || (brk === null && (left <= maxWait || opts.chain))) {
          // the current line is on its last words (or has no break left and is nearly done, or the new line was
          // asked to follow it): let it land, follow it at once
          // (behind whatever is still queued: only lines at least as big as this one are left, and they were first)
          this.dropWhere((q) => q.id === id);
          if (this.queue.length >= MAX_QUEUE) this.drop(0);
          const q: Queued = { id, priority, duck, expires: this.projectedEnd(cur, now) + QUEUE_TTL_S };
          if (opts.chain && this.queue.length > 0) q.after = this.queue[this.queue.length - 1];
          this.queue.push(q);
          return caption;
        }
        if (brk !== null) {
          // finish the phrase ("SPIN MOVE!!"), then the bigger call
          this.cut(cur, brk, BREAK_FADE_S);
          this.start(id, priority, duck, now, brk + BREAK_FADE_S - now);
          return caption;
        }
        // no break in reach: cut now — a line that only just started is swapped out before its first word is
        // out (a quick fade, the bigger call right behind it) rather than heard as a clipped syllable
        const fresh = now - cur.startAt < FRESH_S;
        this.cut(cur, now, fresh ? FRESH_FADE_S : INTERRUPT_FADE_S);
        this.start(id, priority, duck, now, fresh ? FRESH_FADE_S + 0.005 : INTERRUPT_ENTRY_S);
        return caption;
      }
      const explicitWait =
        opts.wait !== undefined && Number.isFinite(opts.wait) ? Math.max(0, opts.wait) : undefined;
      // a lower line is dropped — unless it is chained (it belongs to the call ahead: the replay narration after
      // "TOUCHDOWN!!!" waits for it instead of vanishing), or it was given its own `wait` and the booth will be free
      // within it ("Fourth down… big play here!" right behind "SACKED!! Buried him!" is still news)
      if (
        priority < cur.priority &&
        !opts.chain &&
        (explicitWait === undefined || this.projectedEnd(cur, now) - now > explicitWait)
      )
        return null;
      if (this.queue.some((q) => q.id === id) || cur.id === id) return null; // never stack the same bark
      if (this.queue.length >= MAX_QUEUE) this.drop(0);
      const wait = explicitWait ?? QUEUE_TTL_S;
      const expires = opts.chain ? this.projectedEnd(cur, now) + QUEUE_TTL_S : now + wait;
      const q: Queued = { id, priority, duck, expires };
      if (opts.chain && this.queue.length > 0) q.after = this.queue[this.queue.length - 1];
      this.queue.push(q);
      return caption;
    }
    this.start(id, priority, duck, now);
    return caption;
  }

  /** Called ~10×/s and when a line ends: start the next queued line (scheduled right after the last word). */
  pump(now: number): void {
    const cur = this.speaking;
    if (cur && cur.endsAt <= now) this.release(cur);
    else if (cur) {
      if (this.queue.length === 0 || cur.endsAt - now > CHAIN_LOOKAHEAD_S || cur.startAt > now) return;
      this.expire(now);
      const next = this.queue.shift();
      if (!next) return;
      this.release(cur);
      this.start(next.id, next.priority, next.duck, now, cur.endsAt + CHAIN_BREATH_S - now);
      return;
    }
    this.expire(now);
    const next = this.queue.shift();
    if (next) this.start(next.id, next.priority, next.duck, now);
  }

  /**
   * Drop a line that has not made a sound yet (queued, or scheduled ahead). With `fade` (s), a line already
   * speaking is faded out too (an instant replay skipped mid-narration) — unless it has at most `grace` (s; default
   * `INTERRUPT_GRACE_S`, its last word) left: it lands. True if one was dropped or faded.
   */
  cancel(id: string, fade?: number, grace = INTERRUPT_GRACE_S): boolean {
    let dropped = false;
    for (let i = this.queue.length - 1; i >= 0; i--)
      if (this.queue[i]!.id === id) {
        this.queue.splice(i, 1);
        dropped = true;
      }
    const cur = this.speaking;
    const now = this.ctx.currentTime;
    if (cur && cur.id === id && cur.startAt > now + 1e-4) {
      this.cut(cur, now, 0.005);
      dropped = true;
      this.pump(now);
    } else if (
      cur &&
      cur.id === id &&
      fade !== undefined &&
      Number.isFinite(fade) &&
      cur.endsAt - now > (Number.isFinite(grace) ? Math.max(0, grace) : INTERRUPT_GRACE_S)
    ) {
      this.cut(cur, now, Math.max(0.005, fade));
      dropped = true;
      this.pump(now);
    }
    return dropped;
  }

  /** Stop talking and forget the queue (leaving a match). */
  hush(fade = 0.04): void {
    this.queue.length = 0;
    const now = this.ctx.currentTime;
    if (this.speaking) this.cut(this.speaking, now, fade);
    // the line that just finished may still have a breath of sound left: fade it too
    const t = this.tailing;
    this.tailing = null;
    if (t) this.fadeOut(t, now, fade);
  }

  dispose(): void {
    this.queue.length = 0;
    for (const s of [this.speaking, this.tailing]) {
      if (!s) continue;
      s.src.onended = null;
      try {
        s.src.stop();
      } catch {
        /* already stopped */
      }
      s.src.disconnect();
      s.gain.disconnect();
    }
    this.speaking = this.tailing = null;
  }

  private expire(now: number): void {
    this.dropWhere((q) => q.expires < now);
  }

  /** Remove queued line `i` unsaid; a chained line that was following it goes too. */
  private drop(i: number): void {
    const q = this.queue[i];
    if (!q) return;
    q.dropped = true;
    this.queue.splice(i, 1);
    for (let j = this.queue.length - 1; j >= 0; j--) if (this.queue[j]!.after?.dropped) this.drop(j);
  }

  private dropWhere(pred: (q: Queued) => boolean): void {
    for (let i = this.queue.length - 1; i >= 0; i--)
      if (i < this.queue.length && pred(this.queue[i]!)) this.drop(i);
  }

  /**
   * The current line's next phrase break (context time) at least a moment from now and at most `maxWait` away,
   * leaving more than the grace to go after it (a break right before the end is the end); null when none.
   */
  private nextBreak(cur: Speaking, now: number, maxWait: number): number | null {
    const breaks = this.src.breaks?.(cur.id);
    if (!breaks) return null;
    for (const b of breaks) {
      const t = cur.startAt + b;
      if (!Number.isFinite(t) || t < now + 0.02) continue;
      if (t - now > maxWait || cur.endsAt - t <= INTERRUPT_GRACE_S) return null;
      return t;
    }
    return null;
  }

  /** When the current line and everything queued behind it should be done (context time). */
  private projectedEnd(cur: Speaking, now: number): number {
    let t = Math.max(now, cur.endsAt);
    for (const q of this.queue) {
      const d = this.src.buffer(q.id)?.duration ?? this.src.duration(q.id);
      if (Number.isFinite(d) && d > 0) t += CHAIN_BREATH_S + Math.max(0, d - this.tailOf(q.id, d));
    }
    return t;
  }

  /** Trailing padding of a line (s), clamped to something sane. */
  private tailOf(id: string, duration: number): number {
    const t = this.src.tail?.(id) ?? 0;
    return Number.isFinite(t) && t > 0 ? Math.min(t, MAX_TAIL_S, duration * 0.4) : 0;
  }

  /** A line's last word ended: it is over for the booth (its silent tail plays on, untouched). */
  private release(s: Speaking): void {
    if (this.speaking !== s) return;
    this.speaking = null;
    this.tailing = s;
    this.src.ended?.(s.id, false);
  }

  private fadeOut(s: Speaking, now: number, fade: number): void {
    const f = Math.max(0.005, Number.isFinite(fade) ? fade : INTERRUPT_FADE_S);
    const g = s.gain.gain;
    g.cancelScheduledValues(now);
    g.setValueAtTime(g.value, now);
    g.linearRampToValueAtTime(0, now + f);
    try {
      s.src.stop(now + f + 0.005);
    } catch {
      /* already stopped */
    }
  }

  /** Fade a line out from `at` (now, or a phrase break ahead: it keeps talking until then, hush still fades it). */
  private cut(s: Speaking, at: number, fade = INTERRUPT_FADE_S): void {
    const now = this.ctx.currentTime;
    this.fadeOut(s, at, fade);
    if (this.speaking === s) {
      this.speaking = null;
      if (at > now + 1e-4) {
        this.tailing = s;
        this.src.ended?.(s.id, true, at);
      } else this.src.ended?.(s.id, true);
    }
  }

  private start(id: string, priority: number, duck: boolean, now: number, delay = 0): void {
    const caption = this.src.caption(id) ?? '';
    const buf = this.src.audible() ? this.src.buffer(id) : null;
    const seconds = buf ? buf.duration : this.src.duration(id);
    const line = { id, caption, durationMs: Math.round(Math.max(1.2, seconds + 0.9) * 1000) };
    if (!buf) {
      this.src.emit(line); // caption only (locked, suspended or not decoded yet)
      return;
    }
    const srcNode = this.ctx.createBufferSource();
    srcNode.buffer = buf;
    const gain = this.ctx.createGain();
    gain.gain.value = this.src.trim(id);
    srcNode.connect(gain);
    gain.connect(this.dest);
    const at = now + Math.max(0, delay);
    const s: Speaking = {
      id,
      priority,
      src: srcNode,
      gain,
      startAt: at,
      endsAt: at + buf.duration - this.tailOf(id, buf.duration),
      duck,
    };
    this.speaking = s;
    this.src.emit(line, at > now + 1e-4 ? at : undefined);
    if (duck) this.src.duck(s.endsAt, priority);
    srcNode.onended = () => {
      srcNode.onended = null;
      srcNode.disconnect();
      gain.disconnect();
      if (this.tailing === s) this.tailing = null;
      if (this.speaking === s) {
        this.speaking = null;
        this.src.ended?.(s.id, false);
        this.pump(this.ctx.currentTime);
      }
    };
    srcNode.start(at);
  }
}
